import { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getClassBackgroundColor, getClassBorderColor, getRumaalColor, getTextColorForBackground } from '../../utils/colors';
import { getDurationInSlots, formatTime, isHalfHourTime, getHalfHourOffset } from '../../utils/timeUtils';
import { setDraggedClass, clearDraggedClass } from '../../store/slices/calendarSlice';
import { calculateClassRumaalRank } from '../../utils/rumaal';

const ClassBlock = ({ classItem, onClick, isSpanning = false, spanHeight = null, spanTop = 0 }) => {
  const dispatch = useDispatch();
  const [isDragging, setIsDragging] = useState(false);
  const wasDraggedRef = useRef(false);
  const touchStartRef = useRef(null);
  const backgroundColor = getClassBackgroundColor(classItem.type, classItem.ageGroup);
  const borderColor = getClassBorderColor(classItem.type, classItem.ageGroup);
  // Calculate the class's overall rumaal level from its students (weighted average)
  const calculatedRumaalLevel = calculateClassRumaalRank(classItem.students);
  const rumaalColor = getRumaalColor(calculatedRumaalLevel);
  // Determine text color based on background
  const textColorMode = getTextColorForBackground(backgroundColor);
  const isDarkBackground = textColorMode === 'light';
  const heightSlots = getDurationInSlots(classItem.duration);
  const [slotHeight, setSlotHeight] = useState(160); // Default to md+ size
  
  // Calculate responsive slot height based on screen size
  // Mobile: h-32 = 128px, Small: h-36 = 144px, Medium+: h-40 = 160px
  useEffect(() => {
    const updateSlotHeight = () => {
      const width = window.innerWidth;
      if (width < 640) {
        // Mobile: 128px per slot
        setSlotHeight(128);
      } else if (width < 768) {
        // Small: 144px per slot
        setSlotHeight(144);
      } else {
        // Medium+: 160px per slot
        setSlotHeight(160);
      }
    };
    
    updateSlotHeight();
    window.addEventListener('resize', updateSlotHeight);
    return () => window.removeEventListener('resize', updateSlotHeight);
  }, []);
  
  const height = heightSlots * slotHeight;
  
  // Calculate offset for half-hour start times
  const halfHourOffset = isHalfHourTime(classItem.startTime) ? getHalfHourOffset(classItem.startTime, slotHeight) : 0;
  
  // Calculate capacity progress
  const studentCount = classItem.students?.length || 0;
  const capacity = 12; // Target capacity
  const capacityPercentage = Math.min((studentCount / capacity) * 100, 100);
  const isAtCapacity = studentCount >= capacity;
  const isOverCapacity = studentCount > capacity;
  
  // Determine progress bar color - use pastel colors except when at capacity (red)
  const getProgressBarColor = () => {
    if (isAtCapacity || isOverCapacity) return '#EF4444'; // Red when at or over capacity (12+)
    if (capacityPercentage >= 50) return '#A7F3D0'; // Pastel green above 50%
    return '#D1D5DB'; // Pastel grey under 50%
  };

  const handleDragStart = (e) => {
    if (isSpanning) {
      e.preventDefault();
      return;
    }
    wasDraggedRef.current = false;
    setIsDragging(true);
    dispatch(setDraggedClass(classItem));
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', classItem.id);
    // Use a semi-transparent version as drag image
    e.dataTransfer.setDragImage(e.currentTarget, e.currentTarget.offsetWidth / 2, 10);
  };

  const handleDragEnd = (e) => {
    wasDraggedRef.current = true;
    setIsDragging(false);
    dispatch(clearDraggedClass());
    // Prevent click event from firing after drag
    setTimeout(() => {
      wasDraggedRef.current = false;
    }, 100);
  };

  const handleClick = (e) => {
    // Don't trigger click if we just finished dragging
    if (wasDraggedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick();
  };

  // Touch handlers for mobile drag and drop
  const handleTouchStart = (e) => {
    if (isSpanning) {
      return;
    }
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
    wasDraggedRef.current = false;
  };

  const handleTouchMove = (e) => {
    if (!touchStartRef.current || isSpanning) return;
    
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
    
    // Start dragging if moved more than 10px
    if ((deltaX > 10 || deltaY > 10) && !isDragging) {
      wasDraggedRef.current = true;
      setIsDragging(true);
      dispatch(setDraggedClass(classItem));
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e) => {
    if (!touchStartRef.current || isSpanning) return;
    
    const touch = touchStartRef.current;
    const timeDiff = Date.now() - touch.time;
    
    // If it was a quick tap (< 300ms) and didn't move much, treat as click
    if (timeDiff < 300 && !wasDraggedRef.current) {
      onClick();
    }
    
    touchStartRef.current = null;
    setIsDragging(false);
    dispatch(clearDraggedClass());
    
    // Reset wasDragged after a delay
    setTimeout(() => {
      wasDraggedRef.current = false;
    }, 100);
  };

  // Only show the main block at the start time, spanning blocks are just continuation
  if (isSpanning) {
    // Show progress bar on spanning block for classes > 60 minutes
    const showProgressBar = classItem.duration > 60;
    
    return (
      <div
        className="absolute left-0 right-0 cursor-pointer"
        style={{
          top: `${spanTop}px`,
          height: spanHeight ? `${spanHeight}px` : '100%',
          backgroundColor,
          borderTop: 'none',
          borderBottom: 'none',
          borderLeft: 'none', // Remove left border since we have accent stripe
          borderRight: borderColor !== 'transparent' ? `2px solid ${borderColor}` : 'none',
        }}
        onClick={onClick}
      >
        {/* Left accent stripe - consistent with main block */}
        <div 
          className="absolute left-0 top-0 bottom-0 w-1"
          style={{
            backgroundColor: borderColor !== 'transparent' ? borderColor : rumaalColor,
            opacity: 0.6,
          }}
        />
        
        {/* Capacity progress bar - show on spanning block for classes > 60 minutes */}
        {showProgressBar && (() => {
          const textColorMode = getTextColorForBackground(backgroundColor);
          const isDarkBg = textColorMode === 'light';
          return (
            <div className="absolute bottom-0 left-2.5 right-2.5 pb-1">
              {/* Time above progress bar with enhanced styling */}
              <div className="text-center mb-2 px-1">
                <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md backdrop-blur-sm ${
                  isDarkBg 
                    ? 'bg-white/20 border border-white/30' 
                    : 'bg-gray-50/60'
                }`}>
                  <svg className={`w-3 h-3 ${
                    isDarkBg ? 'text-white/90' : 'text-gray-500'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-xs font-semibold tracking-wide ${
                    isDarkBg ? 'text-white/90' : 'text-gray-700'
                  }`}>
                    {formatTime(classItem.startTime)}
                  </span>
                </div>
              </div>
            
              <div 
                className="h-1.5 rounded-full bg-white shadow-sm overflow-hidden"
                style={{ boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)' }}
              >
                <div
                  className="h-full rounded-full transition-all duration-200"
                  style={{
                    width: `${Math.min(capacityPercentage, 100)}%`,
                    backgroundColor: getProgressBarColor(),
                  }}
                />
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className={`absolute left-0 right-0 rounded-md cursor-move overflow-hidden transition-all duration-200 touch-none ${
        isDragging ? 'opacity-60 scale-[0.98]' : 'hover:shadow-md'
      }`}
      style={{
        top: `${halfHourOffset}px`,
        height: `${height}px`,
        backgroundColor,
        border: borderColor !== 'transparent' ? `2px solid ${borderColor}` : `1px solid rgba(0, 0, 0, 0.08)`,
        borderLeft: 'none', // Remove left border since we have accent stripe
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Left accent stripe - subtle cultural touch */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{
          backgroundColor: borderColor !== 'transparent' ? borderColor : rumaalColor,
          opacity: 0.6,
        }}
      />

      {/* Content */}
      <div className="h-full flex flex-col p-1.5 sm:p-2 md:p-2.5 relative">
        {/* Header with class name and Rumaal badge */}
        <div className="flex items-start justify-between mb-0.5 gap-1">
          <div className="flex-1 min-w-0 overflow-hidden">
            <div 
              className={`text-xs sm:text-sm font-bold leading-tight ${
                isDarkBackground ? 'text-white' : 'text-gray-900'
              }`}
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {classItem.name || `${classItem.type} – ${classItem.ageGroup}`}
            </div>
          </div>
          {/* Rumaal badge - minimal */}
          <div className="flex-shrink-0">
            <div
              className="rounded-full w-4 h-1.5 sm:w-5 sm:h-2"
              style={{
                backgroundColor: rumaalColor,
                border: rumaalColor === '#FFFFFF' ? '1px solid #D1D5DB' : 'none',
              }}
              title={`Rumaal: ${calculatedRumaalLevel}`}
            />
          </div>
        </div>

        {/* Instructor and Student count with icons */}
        <div className={`text-xs sm:text-xs mb-0.5 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-1.5 ${
          isDarkBackground ? 'text-gray-200' : 'text-gray-600'
        }`}>
          {classItem.instructor && (
            <div className="flex items-center gap-1.5 sm:gap-1">
              {/* Instructor icon */}
              <svg className={`w-4 h-4 sm:w-4 sm:h-4 flex-shrink-0 ${
                isDarkBackground ? 'text-gray-300' : 'text-gray-500'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className={`text-xs sm:text-xs ${
                isDarkBackground ? 'text-gray-100' : 'text-gray-800'
              }`}>{classItem.instructor}</span>
            </div>
          )}
          {classItem.instructor && (
            <span className={`hidden sm:inline text-xs sm:text-xs ${isDarkBackground ? 'text-gray-400' : 'text-gray-400'}`}>|</span>
          )}
          <div className="flex items-center gap-1.5 sm:gap-1">
            {/* Student icon */}
            <svg className={`w-4 h-4 sm:w-4 sm:h-4 flex-shrink-0 ${
              isDarkBackground ? 'text-gray-300' : 'text-gray-500'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <span className={`text-xs sm:text-xs ${
              isDarkBackground ? 'text-gray-100' : 'text-gray-800'
            }`}>
              {studentCount} {studentCount === 1 ? 'student' : 'students'}
            </span>
          </div>
        </div>

        {/* Capacity progress bar - only show for classes <= 60 minutes */}
        {classItem.duration <= 60 && (
          <div className="mt-auto">
            {/* Time above progress bar with enhanced styling */}
            <div className="text-center mb-2 px-1">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-50/60 backdrop-blur-sm">
                <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs font-semibold text-gray-700 tracking-wide">
                  {formatTime(classItem.startTime)}
                </span>
              </div>
            </div>
            
            <div 
              className="h-1.5 rounded-full bg-white shadow-sm overflow-hidden"
              style={{ boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)' }}
            >
              <div
                className="h-full rounded-full transition-all duration-200"
                style={{
                  width: `${Math.min(capacityPercentage, 100)}%`,
                  backgroundColor: getProgressBarColor(),
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassBlock;

